import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  AdminPanelSettings as AdminIcon,
  FlightTakeoff as FlightIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const PalletForm = ({ editMode = false }) => {
  const { awb, palletId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedPosicion = searchParams.get('posicion');

  const isEditing = editMode || !!palletId;

  const [vuelo, setVuelo] = useState(null);
  const [posicionesLibres, setPosicionesLibres] = useState([]);
  const [allPosiciones, setAllPosiciones] = useState([]);
  const [usuarios, setUsuarios] = useState({ paletizadores: [], distribuidores: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [originalPallet, setOriginalPallet] = useState(null);
  
  // Loading List (guías en warehouse asignadas a este vuelo) - ÚNICA FUENTE PARA PALETIZAR
  const [loadingListItems, setLoadingListItems] = useState([]);
  
  // Pallets existentes del vuelo (para calcular piezas ya paletizadas)
  const [existingPallets, setExistingPallets] = useState([]);
  
  // Destino seleccionado (para validar que todas las guías tengan el mismo)
  const [selectedDestino, setSelectedDestino] = useState(null);

  const [formData, setFormData] = useState({
    placa: '',
    id_posicion: preselectedPosicion || '',
    tipoPos: '',
    pesoTotalP: '',
    paletizador: '',
    distribuidor: '',
    xl: 'N/A',
    notas2: '',
    guias: [{ guia: '', tpiezas: '', dg: false, un: '', clase: '', destination: '' }],
  });

  useEffect(() => {
    fetchData();
  }, [awb, palletId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const vueloRes = await api.get(`${endpoints.vuelos.detalle}?awb=${awb}`);
      setVuelo(vueloRes.data.data.vuelo);
      
      // Cargar loading list del vuelo - ÚNICA FUENTE para paletizar
      try {
        const loadingRes = await api.get(`${endpoints.warehouse.loading}?flight=${awb}`);
        const items = loadingRes.data.data?.items || [];
        // Filtrar solo planned y staged (no loaded/flown)
        setLoadingListItems(items.filter(i => i.status === 'planned' || i.status === 'staged'));
      } catch (err) {
        console.log('No hay items en loading list para este vuelo');
        setLoadingListItems([]);
      }
      
      // Cargar pallets existentes para calcular piezas ya paletizadas
      try {
        const palletsRes = await api.get(`${endpoints.pallets.list}?awb=${awb}`);
        setExistingPallets(palletsRes.data.data.pallets || []);
      } catch (err) {
        console.log('No hay pallets existentes');
        setExistingPallets([]);
      }

      const posRes = await api.get(
        `${endpoints.posiciones.list}?equipo_id=${vueloRes.data.data.vuelo.equipo_id}&awb=${awb}`
      );
      setPosicionesLibres(posRes.data.data.libres || []);
      setAllPosiciones(posRes.data.data.posiciones || []);

      // Si es modo edición, cargar el pallet
      if (isEditing && palletId) {
        // Verificar que el usuario tiene permisos
        if (!isAdmin && !hasPermission('pallets.edit')) {
          setError('No tiene permisos para editar pallets');
          setLoading(false);
          return;
        }

        const palletsRes = await api.get(`${endpoints.pallets.list}?awb=${awb}`);
        const pallet = palletsRes.data.data.pallets.find((p) => p.id == palletId);
        
        if (pallet) {
          setOriginalPallet(pallet);
          setSelectedDestino(pallet.dfinal);
          
          // Cargar datos del pallet en el formulario
          setFormData({
            placa: pallet.placa || '',
            id_posicion: pallet.id_posicion || '',
            tipoPos: pallet.tipoPos || '',
            pesoTotalP: pallet.pesoTotalP || '',
            paletizador: pallet.paletizador || '',
            distribuidor: pallet.distribuidor || '',
            xl: pallet.xl || 'N/A',
            notas2: pallet.notas2 || '',
            guias: pallet.guias_data?.length > 0 
              ? pallet.guias_data.map(g => ({
                  guia: g.guia || '',
                  tpiezas: g.tpiezas || '',
                  dg: g.dg || false,
                  un: g.un || '',
                  clase: g.clase || '',
                  destination: pallet.dfinal || '',
                }))
              : pallet.guias?.map(g => ({ guia: g, tpiezas: '', dg: false, un: '', clase: '', destination: pallet.dfinal || '' })) 
              || [{ guia: '', tpiezas: '', dg: false, un: '', clase: '', destination: '' }],
          });

          // Agregar la posición actual a las posiciones libres
          const currentPos = posRes.data.data.posiciones.find(p => p.posicion_id === pallet.id_posicion);
          if (currentPos && !posRes.data.data.libres?.find(p => p.posicion_id === pallet.id_posicion)) {
            setPosicionesLibres(prev => [currentPos, ...prev]);
          }
        } else {
          setError('Pallet no encontrado');
        }
      } else if (preselectedPosicion) {
        const posicion = posRes.data.data.posiciones.find(
          (p) => p.posicion_id === preselectedPosicion
        );
        if (posicion) {
          setFormData((prev) => ({
            ...prev,
            id_posicion: posicion.posicion_id,
            tipoPos: posicion.tipo_posicion,
          }));
        }
      }

 
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'id_posicion') {
      const pos = posicionesLibres.find((p) => p.posicion_id === value);
      if (pos) {
        setFormData((prev) => ({ ...prev, tipoPos: pos.tipo_posicion }));
      }
    }
  };

  const handleGuiaChange = (index, field, value) => {
    const newGuias = [...formData.guias];
    newGuias[index][field] = value;
    setFormData((prev) => ({ ...prev, guias: newGuias }));
  };

  // Manejar selección de guía del Loading List
  const handleGuiaSelect = (index, option) => {
    if (option) {
      const newGuias = [...formData.guias];
      // Sugerir las piezas DISPONIBLES (no las totales)
      const piezasSugeridas = option.piecesDisponibles ?? option.pieces ?? '';
      
      newGuias[index] = {
        ...newGuias[index],
        guia: option.guia,
        tpiezas: piezasSugeridas,
        destination: option.destination || '',
        // Guardar info extra para validación
        maxPiezas: option.piecesDisponibles ?? option.pieces ?? 0,
      };
      setFormData((prev) => ({ ...prev, guias: newGuias }));
      
      // Establecer destino si es la primera guía
      if (!selectedDestino && option.destination) {
        setSelectedDestino(option.destination);
      }
    }
  };

  // Calcular piezas ya paletizadas por guía (excluyendo el pallet actual en modo edición)
  const getPiezasPaletizadas = (guiaAwb) => {
    let piezasPaletizadas = 0;
    
    existingPallets.forEach(pallet => {
      // En modo edición, excluir el pallet actual
      if (isEditing && originalPallet && pallet.id === originalPallet.id) {
        return;
      }
      
      // Buscar en guias_data (formato JSON con detalle)
      if (pallet.guias_data && Array.isArray(pallet.guias_data)) {
        const guiaData = pallet.guias_data.find(g => g.guia === guiaAwb);
        if (guiaData) {
          piezasPaletizadas += parseInt(guiaData.tpiezas) || 0;
        }
      } else if (pallet.guias && Array.isArray(pallet.guias)) {
        // Formato antiguo: guias como array de strings "AWB: piezas"
        pallet.guias.forEach(g => {
          if (typeof g === 'string' && g.includes(':')) {
            const [awbPart, piezasPart] = g.split(':').map(s => s.trim());
            if (awbPart === guiaAwb) {
              piezasPaletizadas += parseInt(piezasPart) || 0;
            }
          }
        });
      }
    });
    
    return piezasPaletizadas;
  };

  // Calcular piezas usadas en ESTE formulario (excluyendo la línea actual)
  const getPiezasEnFormulario = (guiaAwb, excludeIndex) => {
    return formData.guias
      .filter((g, i) => i !== excludeIndex && g.guia === guiaAwb)
      .reduce((sum, g) => sum + (parseInt(g.tpiezas) || 0), 0);
  };

  // Obtener piezas disponibles para una guía
  const getPiezasDisponibles = (guiaAwb, currentIndex) => {
    const loadingItem = loadingListItems.find(i => 
      (i.guia || i.cargo_awb || i.reference) === guiaAwb
    );
    
    if (!loadingItem) return 0;
    
    const totalAsignado = loadingItem.pieces_assigned || 0;
    const yaPaletizadas = getPiezasPaletizadas(guiaAwb);
    const enFormulario = getPiezasEnFormulario(guiaAwb, currentIndex);
    
    return Math.max(0, totalAsignado - yaPaletizadas - enFormulario);
  };

  // Obtener opciones de guías del Loading List (filtradas por destino si ya hay una seleccionada)
  const getAvailableGuias = (currentIndex) => {
    const usedAwbs = formData.guias
      .filter((_, i) => i !== currentIndex)
      .map(g => g.guia);
    
    // En modo edición, incluir las guías del pallet original
    const originalGuiasData = isEditing && originalPallet 
      ? (originalPallet.guias_data || originalPallet.guias?.map(g => ({ guia: g })) || [])
      : [];
    
    // Solo Loading List - filtrar por destino si ya hay uno seleccionado
    const loadingOptions = loadingListItems
      .filter(i => {
        const guiaValue = i.guia || i.cargo_awb || i.reference;
        const itemDestination = i.destination || i.dfinal || '';
        
        // Debe tener guía/AWB
        if (!guiaValue) return false;
        
        // Si ya hay un destino seleccionado, filtrar solo guías del mismo destino
        if (selectedDestino && itemDestination && itemDestination !== selectedDestino) {
          return false;
        }
        
        // Calcular piezas disponibles
        const piezasDisponibles = getPiezasDisponibles(guiaValue, currentIndex);
        
        // Si no hay piezas disponibles y no está ya en uso en esta línea, no mostrar
        const currentGuia = formData.guias[currentIndex]?.guia;
        if (piezasDisponibles <= 0 && guiaValue !== currentGuia) {
          return false;
        }
        
        return true;
      })
      .map(i => {
        const guiaValue = i.guia || i.cargo_awb || i.reference;
        const piezasDisponibles = getPiezasDisponibles(guiaValue, currentIndex);
        const piezasPaletizadas = getPiezasPaletizadas(guiaValue);
        
        return {
          guia: guiaValue,
          pieces: i.pieces_assigned,
          piecesDisponibles: piezasDisponibles,
          piecesPaletizadas: piezasPaletizadas,
          weight: i.weight_assigned,
          origin: i.origin || '',
          destination: i.destination || i.dfinal || '',
          status: i.status,
          id: i.id,
        };
      });
    
    // Agregar guías del pallet original que no estén en el loading list
    const existingGuiaNumbers = loadingOptions.map(o => o.guia);
    const originalOptions = originalGuiasData
      .filter(g => {
        const guiaValue = typeof g === 'string' ? g : g.guia;
        // No agregar si ya está en loading options
        return guiaValue && !existingGuiaNumbers.includes(guiaValue);
      })
      .map(g => ({
        guia: typeof g === 'string' ? g : g.guia,
        pieces: typeof g === 'string' ? 0 : (g.tpiezas || 0),
        piecesDisponibles: typeof g === 'string' ? 0 : (g.tpiezas || 0),
        piecesPaletizadas: 0,
        weight: 0,
        origin: '',
        destination: originalPallet?.dfinal || '',
        status: 'loaded',
        id: null,
        isOriginal: true,
      }));
    
    return [...loadingOptions, ...originalOptions];
  };

  // Verificar si hay múltiples destinos en el vuelo
  const vueloDestinos = vuelo?.Destino?.split(';').filter(d => d.trim()) || [];
  const isMultiDestino = vueloDestinos.length > 1;

  // Obtener destinos únicos del Loading List
  const destinosDisponibles = [...new Set(
    loadingListItems
      .map(i => i.destination || i.dfinal)
      .filter(d => d)
  )];

  const addGuia = () => {
    setFormData((prev) => ({
      ...prev,
      guias: [...prev.guias, { guia: '', tpiezas: '', dg: false, un: '', clase: '', destination: selectedDestino || '' }],
    }));
  };

  const removeGuia = (index) => {
    if (formData.guias.length > 1) {
      const newGuias = formData.guias.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, guias: newGuias }));
      
      // Si quitamos todas las guías, resetear destino seleccionado
      if (newGuias.length === 0 || newGuias.every(g => !g.guia)) {
        setSelectedDestino(null);
      }
    }
  };

  // Resetear destino seleccionado
  const resetDestinoSelection = () => {
    setSelectedDestino(null);
    setFormData(prev => ({
      ...prev,
      guias: [{ guia: '', tpiezas: '', dg: false, un: '', clase: '', destination: '' }]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.placa) {
      setError('La placa es requerida');
      return;
    }
    if (!formData.id_posicion) {
      setError('Seleccione una posición');
      return;
    }
    
    const guiasValidas = formData.guias.filter(g => g.guia);
    if (guiasValidas.length === 0) {
      setError('Agregue al menos una guía del Loading List');
      return;
    }

    // Validar que todas las guías estén en el Loading List
    // En modo edición, también aceptar las guías que ya estaban en el pallet original
    const loadingGuias = loadingListItems.map(i => i.guia || i.cargo_awb || i.reference);
    const originalGuias = isEditing && originalPallet 
      ? (originalPallet.guias_data?.map(g => g.guia) || originalPallet.guias || [])
      : [];
    const guiasNoEnLoading = guiasValidas.filter(g => 
      !loadingGuias.includes(g.guia) && !originalGuias.includes(g.guia)
    );
    if (guiasNoEnLoading.length > 0) {
      setError(`Las siguientes guías no están en el Loading List: ${guiasNoEnLoading.map(g => g.guia).join(', ')}`);
      return;
    }

    // Validar que todas las guías tengan el mismo destino
    const destinos = guiasValidas.map(g => g.destination).filter(d => d);
    const destinosUnicos = [...new Set(destinos)];
    if (destinosUnicos.length > 1) {
      setError(`No se pueden mezclar destinos en un mismo pallet. Destinos encontrados: ${destinosUnicos.join(', ')}`);
      return;
    }

    // Validar que no se excedan las piezas disponibles por guía
    const erroresPiezas = [];
    guiasValidas.forEach((g, index) => {
      const loadingItem = loadingListItems.find(i => 
        (i.guia || i.cargo_awb || i.reference) === g.guia
      );
      
      if (loadingItem) {
        const totalAsignado = loadingItem.pieces_assigned || 0;
        const yaPaletizadas = getPiezasPaletizadas(g.guia);
        // No contar las piezas de este formulario para la validación
        const piezasEnFormulario = formData.guias
          .filter((fg, i) => i !== index && fg.guia === g.guia)
          .reduce((sum, fg) => sum + (parseInt(fg.tpiezas) || 0), 0);
        
        const maxDisponibles = totalAsignado - yaPaletizadas - piezasEnFormulario;
        const piezasSolicitadas = parseInt(g.tpiezas) || 0;
        
        if (piezasSolicitadas > maxDisponibles) {
          erroresPiezas.push(
            `Guía ${g.guia}: solicita ${piezasSolicitadas} piezas pero solo hay ${maxDisponibles} disponibles ` +
            `(${totalAsignado} total - ${yaPaletizadas} ya paletizadas)`
          );
        }
      }
    });
    
    if (erroresPiezas.length > 0) {
      setError(`Excede piezas disponibles:\n${erroresPiezas.join('\n')}`);
      return;
    }

    const destinoFinal = selectedDestino || destinosUnicos[0] || vuelo?.Destino?.split(';')[0] || '';

    try {
      setSaving(true);

      if (isEditing && palletId) {
        // Modo edición - actualizar pallet existente
        await api.put(`${endpoints.pallets.update}?id=${palletId}`, {
          placa: formData.placa,
          awb: awb,
          tipoPos: formData.tipoPos,
          id_posicion: formData.id_posicion,
          dfinal: destinoFinal,
          xl: formData.xl,
          notas2: formData.notas2,
          pesoTotalP: parseFloat(formData.pesoTotalP) || 0,
          paletizador: formData.paletizador,
          distribuidor: formData.distribuidor,
          guia: guiasValidas.map(g => g.guia),
          guias: guiasValidas.map(g => ({
            guia: g.guia,
            tpiezas: parseInt(g.tpiezas) || 0,
            dg: g.dg ? 1 : 0,
            un: g.un || '',
            clase: g.clase || '',
          })),
        });
      } else {
        // Modo creación - crear nuevo pallet con todas las guías
        await api.post(endpoints.pallets.create, {
          placa: formData.placa,
          awb: awb,
          guia: guiasValidas.map(g => g.guia),
          tpiezas: guiasValidas.reduce((sum, g) => sum + (parseInt(g.tpiezas) || 0), 0),
          tipoPos: formData.tipoPos,
          id_posicion: formData.id_posicion,
          dfinal: destinoFinal,
          dg: guiasValidas.some(g => g.dg) ? 1 : 0,
          un: guiasValidas.filter(g => g.dg).map(g => g.un).filter(u => u).join(','),
          clase: guiasValidas.filter(g => g.dg).map(g => g.clase).filter(c => c).join(','),
          xl: formData.xl,
          notas2: formData.notas2,
          pesoTotalP: parseFloat(formData.pesoTotalP) || 0,
          paletizador: formData.paletizador,
          distribuidor: formData.distribuidor,
          // Detalle de piezas por guía
          guias: guiasValidas.map(g => ({
            guia: g.guia,
            tpiezas: parseInt(g.tpiezas) || 0,
            dg: g.dg ? 1 : 0,
            un: g.un || '',
            clase: g.clase || '',
          })),
        });
      }

      navigate(`/vuelos/${awb}/gestionar`);
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar el pallet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: isEditing 
            ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)'
            : 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isEditing && <AdminIcon sx={{ fontSize: 40 }} />}
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {isEditing ? `Editar Pallet: ${originalPallet?.placa || formData.placa}` : 'Nuevo Pallet'}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                Vuelo: <strong>{awb}</strong> | Destino: <strong>{vuelo?.Destino}</strong>
                {isEditing && ' | Modo Administrador'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {isEditing && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<AdminIcon />}>
          <strong>Modo Edición (Admin):</strong> Está modificando un pallet ya cargado. 
          Los cambios afectarán los datos del vuelo.
        </Alert>
      )}

      {/* Alerta importante sobre Loading List */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <strong>Regla de Negocio:</strong> Solo se pueden paletizar guías que estén en el Loading List 
        (carga recibida en warehouse y asignada a este vuelo). Un pallet solo puede contener guías del mismo destino.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Alerta si el vuelo es multi-destino */}
      {isMultiDestino && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Vuelo Multi-Destino:</strong> Este vuelo tiene múltiples destinos ({vueloDestinos.join(', ')}). 
          Recuerde que cada pallet solo puede contener guías para <strong>un mismo destino</strong>.
          {selectedDestino && (
            <>
              <br />
              <strong>Destino seleccionado para este pallet:</strong> {selectedDestino}
             
            </>
          )}
        </Alert>
      )}

      {loadingListItems.length === 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Sin carga disponible:</strong> No hay guías en el Loading List para este vuelo. 
          Primero debe recibir carga en el Warehouse y asignarla a este vuelo.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Datos básicos */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Número de Placa"
                  name="placa"
                  value={formData.placa}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder="Ej: PMC-12345"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Posición</InputLabel>
                  <Select
                    name="id_posicion"
                    value={formData.id_posicion}
                    onChange={handleInputChange}
                    label="Posición"
                  >
                    <MenuItem value="">Seleccione posición</MenuItem>
                    {posicionesLibres.map((pos) => (
                      <MenuItem key={pos.posicion_id} value={pos.posicion_id}>
                        {pos.tipo_posicion} - {pos.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Peso Total (kg)"
                  name="pesoTotalP"
                  type="number"
                  value={formData.pesoTotalP}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="0"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Oversize</InputLabel>
                  <Select name="xl" value={formData.xl} onChange={handleInputChange} label="Oversize">
                    <MenuItem value="N/A">No aplica</MenuItem>
                    <MenuItem value="XL">XL (Oversize)</MenuItem>
                    <MenuItem value="XL-W">XL-W (Wide)</MenuItem>
                    <MenuItem value="XL-L">XL-L (Long)</MenuItem>
                    <MenuItem value="XL-H">XL-H (High)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Paletizador</InputLabel>
                  <Select
                    name="paletizador"
                    value={formData.paletizador}
                    onChange={handleInputChange}
                    label="Paletizador"
                  >
                    <MenuItem value="">Seleccione</MenuItem>
                    {usuarios.paletizadores.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Distribuidor</InputLabel>
                  <Select
                    name="distribuidor"
                    value={formData.distribuidor}
                    onChange={handleInputChange}
                    label="Distribuidor"
                  >
                    <MenuItem value="">Seleccione</MenuItem>
                    {usuarios.distribuidores.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notas"
                  name="notas2"
                  value={formData.notas2}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Notas adicionales..."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Resumen de piezas paletizadas */}
            {existingPallets.length > 0 && (
              <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Guías parcialmente paletizadas en este vuelo:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(() => {
                    // Calcular piezas paletizadas por guía
                    const resumenGuias = {};
                    existingPallets.forEach(pallet => {
                      if (isEditing && originalPallet && pallet.id === originalPallet.id) return;
                      if (pallet.guias_data && Array.isArray(pallet.guias_data)) {
                        pallet.guias_data.forEach(g => {
                          if (!resumenGuias[g.guia]) {
                            resumenGuias[g.guia] = { paletizadas: 0, total: 0 };
                          }
                          resumenGuias[g.guia].paletizadas += parseInt(g.tpiezas) || 0;
                        });
                      }
                    });
                    
                    // Agregar total del loading list
                    loadingListItems.forEach(item => {
                      const guiaValue = item.guia || item.cargo_awb || item.reference;
                      if (resumenGuias[guiaValue]) {
                        resumenGuias[guiaValue].total = item.pieces_assigned || 0;
                      }
                    });
                    
                    const guiasConPiezas = Object.entries(resumenGuias).filter(([_, v]) => v.paletizadas > 0);
                    
                    if (guiasConPiezas.length === 0) {
                      return <Typography variant="body2">No hay guías paletizadas aún</Typography>;
                    }
                    
                    return guiasConPiezas.map(([guia, info]) => (
                      <Chip
                        key={guia}
                        label={`${guia}: ${info.paletizadas}/${info.total || '?'} pzs`}
                        size="small"
                        color={info.paletizadas >= info.total ? 'default' : 'warning'}
                        variant={info.paletizadas >= info.total ? 'filled' : 'outlined'}
                      />
                    ));
                  })()}
                </Box>
              </Alert>
            )}

            {/* Guías del Loading List */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Guías del Loading List
                  </Typography>
                  <Chip 
                    icon={<FlightIcon />} 
                    label={`${loadingListItems.length} disponibles`} 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                  />
                  {selectedDestino && (
                    <Chip 
                      label={`Destino: ${selectedDestino}`} 
                      color="primary" 
                      size="small"
                    />
                  )}
                </Box>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />} 
                  onClick={addGuia}
                  disabled={loadingListItems.length === 0}
                >
                  Agregar Guía
                </Button>
              </Box>

              {formData.guias.map((guia, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="medium">Guía {index + 1}</Typography>
                      {guia.destination && (
                        <Chip label={`→ ${guia.destination}`} size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                    {formData.guias.length > 1 && (
                      <IconButton color="error" onClick={() => removeGuia(index)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        sx={{ minWidth: 280 }}
                        options={getAvailableGuias(index)}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.guia || '';
                        }}
                        value={getAvailableGuias(index).find(o => o.guia === guia.guia) || null}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            handleGuiaSelect(index, newValue);
                          } else {
                            handleGuiaChange(index, 'guia', '');
                            handleGuiaChange(index, 'tpiezas', '');
                            handleGuiaChange(index, 'destination', '');
                          }
                        }}
                        isOptionEqualToValue={(option, value) => option.guia === value?.guia}
                        noOptionsText={
                          loadingListItems.length === 0 
                            ? "No hay guías en el Loading List" 
                            : selectedDestino 
                              ? `No hay más guías para ${selectedDestino}`
                              : "No hay guías disponibles"
                        }
                        renderOption={(props, option) => {
                          const { key, ...rest } = props;
                          const disponibles = option.piecesDisponibles ?? option.pieces ?? 0;
                          const paletizadas = option.piecesPaletizadas ?? 0;
                          const total = option.pieces ?? 0;
                          
                          return (
                            <li key={key} {...rest}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography fontWeight="bold">{option.guia}</Typography>
                                  <Chip 
                                    label={option.destination || 'Sin destino'} 
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                  <Chip 
                                    label={`${disponibles} disponibles`}
                                    size="small"
                                    color={disponibles > 0 ? 'primary' : 'error'}
                                  />
                                  {paletizadas > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({paletizadas} de {total} ya paletizadas)
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {parseFloat(option.weight || 0).toLocaleString()} kg total
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Seleccionar Guía"
                            required
                            fullWidth
                            placeholder="Seleccione del Loading List"
                            size="small"
                            helperText="Solo guías del Loading List"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {(() => {
                        const maxDisponibles = guia.guia ? getPiezasDisponibles(guia.guia, index) + (parseInt(guia.tpiezas) || 0) : 0;
                        const valorActual = parseInt(guia.tpiezas) || 0;
                        const excedeMax = valorActual > maxDisponibles && maxDisponibles > 0;
                        
                        return (
                          <TextField
                            label="Piezas"
                            type="number"
                            value={guia.tpiezas}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              if (newValue <= maxDisponibles || maxDisponibles === 0) {
                                handleGuiaChange(index, 'tpiezas', e.target.value);
                              } else {
                                // Mostrar alerta si excede
                                handleGuiaChange(index, 'tpiezas', maxDisponibles.toString());
                              }
                            }}
                            fullWidth
                            required
                            placeholder="0"
                            size="small"
                            error={excedeMax}
                            helperText={
                              guia.guia && maxDisponibles > 0
                                ? `Máximo disponible: ${maxDisponibles}`
                                : excedeMax 
                                  ? `No puede exceder ${maxDisponibles} piezas`
                                  : ''
                            }
                            InputProps={{
                              inputProps: { 
                                min: 0, 
                                max: maxDisponibles > 0 ? maxDisponibles : undefined 
                              }
                            }}
                          />
                        );
                      })()}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={guia.dg}
                            onChange={(e) => handleGuiaChange(index, 'dg', e.target.checked)}
                            color="warning"
                          />
                        }
                        label="Dangerous Goods"
                      />
                    </Grid>
                  </Grid>

                  {guia.dg && (
                    <Paper
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'warning.50',
                        border: 1,
                        borderColor: 'warning.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WarningIcon color="warning" />
                        <Typography variant="subtitle2" fontWeight="bold">
                          Información de Mercancía Peligrosa
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            label="UN Number"
                            value={guia.un}
                            onChange={(e) => handleGuiaChange(index, 'un', e.target.value)}
                            fullWidth
                            placeholder="UN1234"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Clase"
                            value={guia.clase}
                            onChange={(e) => handleGuiaChange(index, 'clase', e.target.value)}
                            fullWidth
                            placeholder="3, 4.1, 8, 9..."
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Paper>
              ))}
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color={isEditing ? 'warning' : 'success'}
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving || loadingListItems.length === 0}
                sx={{ flex: 1 }}
              >
                {saving 
                  ? (isEditing ? 'Actualizando...' : 'Guardando...') 
                  : (isEditing ? 'Actualizar Pallet' : 'Guardar Pallet')}
              </Button>
              <Button
                component={Link}
                to={`/vuelos/${awb}/gestionar`}
                variant="contained"
                color="inherit"
                size="large"
                startIcon={<BackIcon />}
              >
                Cancelar
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PalletForm;
